import React, { createContext, useState, useEffect } from 'react';
import { apiFetch } from '../api/client';
import { decodeJwtPayload } from '../api/jwt';
import { clearEntitlementsCache } from '../hooks/useEntitlements';
import { clearRoleCapabilitiesCache } from '../hooks/useRoleCapabilities';

export const AuthContext = createContext();

// Reads the `tenant_id` claim out of a session JWT purely for display
// purposes (e.g. showing "which hotel am I logged into" somewhere in the
// UI). See api/jwt.js's module header — this is NEVER a source of truth
// for authorization, only a convenience read of what the backend already
// put in the token it issued.
function tenantIdFromToken(token) {
  const payload = decodeJwtPayload(token);
  return payload?.tenant_id ?? null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Platform support-session hand-off: the superadmin console opens
    // /app#support-token=<jwt> in a new tab (see
    // docs/platform-admin-architecture.md §4 and pages/platform/TenantDetail.js).
    // Adopt that token as this tab's session, derive a display user from its
    // claims, and strip the fragment so it isn't left in history / re-used
    // on refresh. The token is short-lived and server-validated on every
    // request against the platform_admin_sessions row.
    const hash = window.location.hash || '';
    const m = hash.match(/support-token=([^&]+)/);
    if (m) {
      const supportToken = decodeURIComponent(m[1]);
      const claims = decodeJwtPayload(supportToken) || {};
      // Display role is 'superuser' so the nav shows everything — nav
      // visibility is UX-only (see config/roleModules.js's header); the
      // Worker is the real gate (it re-derives the impersonated user's
      // actual role, and bypasses role groups entirely for full_access).
      const displayUser = {
        id: claims.sub,
        username: 'TALIT support',
        name: 'TALIT support',
        role: 'superuser',
        _support: true,
        _mode: claims.mode || 'impersonate',
      };
      localStorage.setItem('token', supportToken);
      localStorage.setItem('user', JSON.stringify(displayUser));
      // Remove the fragment without a reload.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setUser(displayUser);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // Persists a successful login/choose-business response (token + user) and
  // updates context state. Shared by login(), chooseBusiness(), and the
  // single-tenant fast path of ownerLogin() — every path that ends in a
  // real, usable session token funnels through here.
  const storeSession = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    // A different login must never inherit the previous person's cached rights.
    clearRoleCapabilitiesCache();
    setUser(data.user);
  };

  // Staff login: POST /auth/login { tenant, username, password }.
  const login = async (tenantSlug, username, password) => {
    const response = await apiFetch('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant: tenantSlug, username, password })
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) {
      storeSession(data);
      return true;
    }
    throw new Error(data.message || 'Invalid username or password');
  };

  // Owner login: POST /auth/owner-login with exactly one of email/phone.
  // Three possible outcomes from the backend:
  //   - single tenant  -> { success: true, token, user }        (normal session)
  //   - zero tenants   -> 404 { success: false, message }
  //   - multiple       -> { success: true, chooseBusiness: true, token, tenants: [...] }
  // Callers should check the returned `chooseBusiness` flag: if true, show a
  // business picker and call chooseBusiness() with the token+chosen tenant
  // id; otherwise the caller is already logged in.
  const ownerLogin = async (identifier, password, isPhone) => {
    const body = isPhone
      ? { phone: identifier, password }
      : { email: identifier, password };
    const response = await apiFetch('/auth/owner-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Invalid credentials');
    }
    if (data.chooseBusiness) {
      // Intermediate state — NOT a session token, do not persist it as one.
      return { chooseBusiness: true, token: data.token, tenants: data.tenants || [] };
    }
    storeSession(data);
    return { chooseBusiness: false };
  };

  // Resolves the short-lived "choose business" token from ownerLogin() into
  // a real session: POST /auth/choose-business { token, tenant_id }.
  const chooseBusiness = async (chooseBusinessToken, tenantId) => {
    const response = await apiFetch('/auth/choose-business', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: chooseBusinessToken, tenant_id: tenantId })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Invalid or expired selection');
    }
    storeSession(data);
    return true;
  };

  // Self-serve sign-up: POST /auth/register. Returns the raw parsed response
  // body either way (success or failure) so the calling page can render the
  // backend's own message (field-level 400, 409 duplicate email, or the
  // 201 success payload with emailSent/tenant).
  const register = async (fields) => {
    const response = await apiFetch('/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields)
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, ...data };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearEntitlementsCache();
    clearRoleCapabilitiesCache();
    setUser(null);
  };

  const tenantId = tenantIdFromToken(localStorage.getItem('token'));

  return (
    <AuthContext.Provider
      value={{ user, loading, tenantId, login, ownerLogin, chooseBusiness, register, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
